import { useEngineStatus } from '@/hooks/useEngineStatus';

/**
 * EngineStatusIndicator
 * ────────────────────────────────────────────────────────────
 * A compact status panel shown on the login screen. Reads the live engine
 * singletons (via useEngineStatus) to display two lights:
 *   - Trading bots: running (count) or stopped
 *   - cTrader gateway: connected (n/m) or not connected
 * It stays live without any sign-in, so the user can see the bots are
 * alive in the background as soon as the page loads.
 */
export default function EngineStatusIndicator() {
  const status = useEngineStatus();

  return (
    <div className="w-full bg-background-100 border border-background-200/70 rounded-xl p-4 mb-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-foreground-600">Engine Status</span>
        <span
          className={`text-[10px] font-medium flex items-center gap-1.5 whitespace-nowrap ${
            status.anyBotRunning ? 'text-green-600' : 'text-foreground-500'
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${
              status.anyBotRunning ? 'bg-green-500 animate-pulse' : 'bg-foreground-300'
            }`}
          ></span>
          {status.anyBotRunning ? 'Live' : 'Idle'}
        </span>
      </div>

      <div className="space-y-2.5">
        <StatusRow
          label="Trading bots"
          running={status.anyBotRunning}
          detail={
            status.anyBotRunning
              ? `${status.totalBotsRunning} running`
              : 'stopped'
          }
        />
        <StatusRow
          label="cTrader gateway"
          running={status.gatewayConnected}
          detail={
            status.gatewayConnected
              ? `${status.streamsConnected}/${status.streamsTotal} connected`
              : 'not connected'
          }
        />
      </div>

      <div className="mt-3 pt-3 border-t border-background-200/70 flex items-center justify-between">
        <span className="text-xs text-foreground-500">Uptime</span>
        <span
          className={`text-xs font-medium tabular-nums whitespace-nowrap ${
            status.anyBotRunning ? 'text-foreground-700' : 'text-foreground-500'
          }`}
        >
          {status.anyBotRunning ? formatUptime(status.uptimeMs) : '—'}
        </span>
      </div>
    </div>
  );
}

function StatusRow({
  label,
  running,
  detail,
}: {
  label: string;
  running: boolean;
  detail: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span
          className={`w-2.5 h-2.5 rounded-full ${
            running ? 'bg-green-500 animate-pulse' : 'bg-foreground-300'
          }`}
        ></span>
        <span className="text-xs text-foreground-700">{label}</span>
      </div>
      <span className={`text-xs whitespace-nowrap ${running ? 'text-green-600' : 'text-foreground-500'}`}>
        {detail}
      </span>
    </div>
  );
}

/**
 * Format a millisecond duration as a compact human-readable uptime string.
 * Examples: "48s", "5m 12s", "2h 05m", "1d 3h".
 */
function formatUptime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  }
  return `${seconds}s`;
}